//@flow
import React, { PureComponent } from 'react';
import { themr } from 'react-css-themr';
import { connect } from 'react-redux';
import { Checkbox } from 'common/components/core/checkbox';
import { translate } from 'react-i18next';
import specificTheme from './ContractMasterContainer.scss';
import compose from 'recompose/compose';
import ContractActions from 'common/redux/actions/contract';
import AppPreferencesActions from 'common/redux/actions/appPreferences';
import SkillActions from 'common/redux/actions/skill';
import { teamsSelector } from 'common/redux/selectors/teamSelector';
import ToastActions from 'common/redux/actions/toast';

import DefaultExpanderArrow from 'common/components/core/common/ExpanderArrow';
import {
    paginatedTableWithExpanderFactory,
    CellDataTypes
} from 'common/components/core/table';

import { ConnectedTeamSelectorFilter } from 'common/entity/components/TeamSelectorFilter';

import {
    clientSelector,
    isAddEntityActive as isAddEntityActiveSelector,
    isImportEntityActive as isImportEntityActiveSelector,
    isExportEntityActive as isExportEntityActiveSelector,
    editEntityIdSelector
} from 'common/redux/selectors/queryParamsSelectors';

import FuelTypeFilter from 'common/entity/components/FuelTypeFilter';
import ContractSelectors from 'common/redux/selectors/contract';
import { getEntitySettings } from 'common/redux/selectors/clientSelector';
import { getEntityTablePreferences } from 'common/redux/selectors/appPreferences';

import { withRouter } from 'common/components/core/router';
import TeamList from 'common/components/core/common/TeamList';

import ContractVersionsTable from './ContractVersionsTable';

import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';

import theme from 'common/entity/BulkEditContainer.scss';

import { dashboardColors } from 'common/enums/ColorEnum';
import { entityMasterFactory } from 'common/entity/generic/MasterContainer';
import MasterContainerHOC from 'common/entity/generic/MasterContainerHOC';

import type { Contract as MasterContract } from 'common/models/internal/MasterContract';
import {
    ContractTypeEnum,
    FleetTypeEnum
} from 'common/models/internal/MasterContract';

import type { EntitySettings } from 'common/models/internal/EntitySettings';
import type { TFunction } from 'react-i18next';
import type { Skill } from 'common/models/internal/Skill';

import { isGlobalPersonnelSelector } from 'common/redux/selectors/sessionSelector';
import { RoleOperationEnum } from 'common/modules/rbac';

const ENTITY_TABLE_ID = 'ContractMaster';

const DEFAULT_COLUMNS = [
    {
        id: 'selector',
        Cell: props => {
            const {
                tdProps: {
                    rest: { isChecked, onCheckboxClick }
                }
            } = props;
            return (
                <Checkbox
                    checkboxColor={dashboardColors.darkSkyBlue.name}
                    className={theme.selectionCell}
                    theme={theme}
                    checked={isChecked}
                    onChange={onCheckboxClick}
                />
            );
        },
        sortable: false,
        filterable: false,
        width: 40
    },
    {
        headerTextId: 'contractTable.id',
        accessor: 'id',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'contractTable.code',
        accessor: 'code',
        infixSearchable: true,
        show: false
    },
    {
        headerTextId: 'contractTable.name',
        accessor: 'name',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'contractTable.transporterId',
        id: 'transporterId',
        accessor: 'transporterId.transporterId',
        sortable: false,
        filterable: false
    },
    {
        headerTextId: 'contractTable.transporter',
        id: 'transporter',
        accessor: 'transporter.name',
        fieldId: 'transporter',
        sortable: false,
        filterable: false
    },
    {
        headerTextId: 'contractTable.vehicleModelIds',
        id: 'vehicleModelIds',
        fieldId: 'vehicleModelIds',
        Cell: props => {
            const {
                original: { vehicleModelIds }
            } = props;
            return vehicleModelIds ? (
                <>
                    {vehicleModelIds
                        .map(vehicleModelId => vehicleModelId?.modelId)
                        .filter(vehicleModelId => !!vehicleModelId)
                        .join(', ')}
                </>
            ) : null;
        },
        sortable: false,
        filterable: false
    },
    {
        headerTextId: 'contractTable.vehicleModels',
        id: 'vehicleModel',
        fieldId: 'vehicleModel',
        Cell: props => {
            const {
                original: { vehicleModels }
            } = props;
            return vehicleModels ? (
                <>
                    {vehicleModels
                        .map(vehicleModel => vehicleModel?.name)
                        .filter(vehicleModel => !!vehicleModel)
                        .join(', ')}
                </>
            ) : null;
        },
        sortable: false,
        filterable: false
    },
    {
        headerTextId: 'contractTable.riderPersonaIds',
        id: 'riderPersonaIds',
        fieldId: 'riderPersonaIds',
        Cell: props => {
            const {
                original: { riderPersonaIds }
            } = props;
            return riderPersonaIds ? (
                <>
                    {riderPersonaIds
                        .map(riderPersonaId => riderPersonaId?.modelId)
                        .filter(riderPersonaId => !!riderPersonaId)
                        .join(', ')}
                </>
            ) : null;
        },
        sortable: false,
        filterable: false
    },
    {
        headerTextId: 'contractTable.riderPersonas',
        id: 'riderPersona',
        fieldId: 'riderPersona',
        Cell: props => {
            const {
                original: { riderPersonas }
            } = props;
            return riderPersonas ? (
                <>
                    {riderPersonas
                        .map(riderPersona => riderPersona?.name)
                        .filter(riderPersona => !!riderPersona)
                        .join(', ')}
                </>
            ) : null;
        },
        sortable: false,
        filterable: false
    },
    {
        headerTextId: 'contractTable.type',
        accessor: 'type',
        minWidth: 100,
        FilterComponent: FuelTypeFilter,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('type')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        getFilterOptions: () => {
            const options = ContractTypeEnum.getOptions({
                translationKey: 'masterContractProperties.types'
            });
            return options;
        }
    },
    {
        headerTextId: 'contractTable.fleetType',
        accessor: 'fleetType',
        minWidth: 100,
        FilterComponent: FuelTypeFilter,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('fleetType')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        getFilterOptions: () => {
            const options = FleetTypeEnum.getOptions({
                translationKey: 'masterContractProperties.fleetTypes'
            });
            return options;
        }
    },
    {
        headerTextId: 'contractTable.teams',
        id: 'teams',
        accessor: 'teams',
        sortable: false,
        minWidth: 200,
        FilterComponent: ConnectedTeamSelectorFilter,
        getCustomFilterInfo: ({ clientId, filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            const teamValueFilter = values.map(team => ({
                clientId,
                teamId: team
            }));

            return new FilterBuilder()
                .withName('teams')
                .withOperation(FilterOperation.Contains)
                .withValues(teamValueFilter)
                .withAllowEmptyOrNull(allowEmptyOrNull)
                .withCaseSensitive(true);
        },
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { theme, teams }
                }
            } = props;
            const teamIds = original.teams;
            return <TeamList teams={teams} teamIds={teamIds} theme={theme} />;
        }
    },
    {
        headerTextId: 'contractTable.validTill',
        accessor: 'expiryDate',
        id: 'expiryDate',
        filterable: false,
        dataType: CellDataTypes.DateTime
    },
    {
        headerTextId: 'contractTable.validFrom',
        accessor: 'startDate',
        id: 'startDate',
        filterable: false,
        dataType: CellDataTypes.DateTime
    },
    {
        headerTextId: 'contractTable.currency',
        accessor: 'currency',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'contractTable.emailAddresses',
        id: 'emailAddresses',
        accessor: 'emailAddress.emailAddress',
        sortable: false,
        filterable: false,
        minWidth: 250,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { theme }
                }
            } = props;
            const { emailAddresses } = (original: MasterContract);

            const cellText = emailAddresses
                .map(emailAddress => emailAddress?.emailAddress || '')
                .join(', ');

            return (
                <div title={cellText} className={theme.cellText}>
                    {cellText}
                </div>
            );
        }
    }
];

type PaginationInfoDataType = {
    currentPage: number,
    numberOfPages: number,
    total: number
};

type ContractsDataType = {
    contracts: Array<MasterContract>,
    paginationInfo: PaginationInfoDataType
};

type Props = {
    clientId: string,
    theme: Object,
    data: ContractsDataType,
    entitySettings: ?EntitySettings,
    isGlobalPersonnel: any,
    tableSettings: ?Array<Object>,
    t: TFunction,
    skills: Array<Skill>,
    fetchSkills: ({ clientId: string }) => void,
    fetchOptions: () => {}
};

const ENTITY_NAME_ID = 'masterContract';
const ENTITY_EXPORT_ID = 'CONTRACT_MASTER';

const ExpanderArrow = props => {
    return (
        <DefaultExpanderArrow
            {...props}
            theme={specificTheme}
            className={specificTheme.expanderBtn}
        />
    );
};
const PaginatedTableWithExpander = paginatedTableWithExpanderFactory({
    ExpanderArrow
});
const ContractMaster = entityMasterFactory({
    PaginatedTableComponent: PaginatedTableWithExpander,
    COLUMNS: DEFAULT_COLUMNS,
    options: {
        entityTableId: 'ContractMaster',
        operations: {
            edit: RoleOperationEnum.UPDATE_CONTRACT,
            add: RoleOperationEnum.UPDATE_CONTRACT,
            export: RoleOperationEnum.EXPORT_CONTRACT
        }
    }
});
class ContractMasterContainer extends PureComponent<Props> {
    componentDidMount = () => {
        this.fetchOtherResources();
    };

    componentDidUpdate = (prevProps: Props) => {
        const { clientId } = this.props;
        if (clientId !== prevProps.clientId) {
            this.fetchOtherResources();
        }
    };

    fetchOtherResources = () => {
        const { clientId, fetchSkills } = this.props;
        fetchSkills({ clientId });
    };

    getTdProps = (state, rowInfo) => {
        if (!rowInfo) {
            return {};
        }
        const {
            original: { id }
        } = rowInfo;
        const {
            t,
            teams,
            entitySettings,
            selectedRows,
            toggleRowSelection,
            theme,
            skills,
            isGlobalPersonnel
        } = this.props;
        const isChecked = selectedRows.indexOf(id) >= 0;
        return {
            onCheckboxClick: () => {
                toggleRowSelection(id);
            },
            t,
            teams,
            entitySettings,
            theme,
            isChecked,
            skills,
            isGlobalPersonnel
        };
    };

    renderSubTable = row => {
        const { theme, t } = this.props;
        const { original } = row;
        const { activeVersion } = original;
        return (
            <div className={theme.subTable}>
                <ContractVersionsTable
                    row={row}
                    contractId={row.original.id}
                    activeVersion={activeVersion}
                    t={t}
                />
            </div>
        );
    };

    render() {
        const { clientId, fetchOptions } = this.props;
        const contractFetchOptions = {
            clientId,
            include: 'TRANSPORTER, VEHICLEMODELS, RIDERPERSONAS',
            ...(fetchOptions || {})
        };
        return (
            <ContractMaster
                {...this.props}
                emptyStateImageSrc="/img/contract-empty.png"
                entityNameId={ENTITY_NAME_ID}
                entityExportId={ENTITY_EXPORT_ID}
                entityId={'contractId'}
                entitySettingsName={'contractSettings'}
                isEditEntityActive={false}
                SubComponent={this.renderSubTable}
                fetchOptions={contractFetchOptions}
                getTdProps={this.getTdProps}
                shouldHideAddEntity
                shouldHideEditEntity
                shouldHideDeleteEntity
            />
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    const clientId = clientSelector(state, ownProps);
    const isLoading = ContractSelectors.isEntitiesLoading(state, ownProps);
    const data = ContractSelectors.getEntities(state, ownProps);
    const isAddEntityActive = isAddEntityActiveSelector(state, ownProps);
    const entityIdToEdit = editEntityIdSelector(state, ownProps);
    const entitySettings = getEntitySettings(state, ownProps);
    const tableSettings = getEntityTablePreferences(state, ENTITY_TABLE_ID);
    const isImportEntityActive = isImportEntityActiveSelector(state, ownProps);
    const isExportEntityActive = isExportEntityActiveSelector(state, ownProps);
    const isGlobalPersonnel = isGlobalPersonnelSelector(state, ownProps);
    const teams = teamsSelector(state, ownProps);

    return {
        clientId,
        isLoading,
        data,
        isAddEntityActive,
        entityIdToEdit,
        entitySettings,
        tableSettings,
        isImportEntityActive,
        teams,
        isExportEntityActive,
        isGlobalPersonnel
    };
};

const withRedux = connect(mapStateToProps, {
    fetchEntities: ContractActions.fetchActiveEntities,
    showToastAction: ToastActions.showToast,
    updateToastAction: ToastActions.updateToast,
    hideToastAction: ToastActions.hideToast,
    fetchSkills: SkillActions.fetchEntities,
    disableEntity: ContractActions.disableEntity,
    clearData: ContractActions.clearData,
    saveTableSettings: AppPreferencesActions.saveEntityTablePreferences
});

const enhance = compose(
    themr(null, theme),
    withRouter,
    withRedux,
    translate('entityManagement'),
    MasterContainerHOC
);
const ThemedConnectedContractMasterContainer = enhance(ContractMasterContainer);

export { ThemedConnectedContractMasterContainer as ContractMasterContainer };
export default ThemedConnectedContractMasterContainer;
